/**
 * TeamManagement.js — Vendor-parity Team page.
 *
 * Matches the AriaOne vendor Team UI (see screenshot 2026-04-21) with:
 *   · header (title, subtitle, member count pill, Refresh, Add User)
 *   · inline "Add Team Member" panel (full name · email · password)
 *   · member card grid: avatar initials · name/email ·
 *       Self/Owner/Active pills · editable role selector ·
 *       Medical / Non-Medical badge · Schedulable toggle ·
 *       Linked / Link-NPI badge · edit · reset-password · delete
 *   · loading / error / empty / read-only states
 *   · toast feedback via sonner
 *
 * Wired EXCLUSIVELY to:
 *   - teamStore  → canonical state (shared with Roles tab)
 *   - teamApi    → vendor-contract paths (AriaOne §14)
 *   - teamAdapter→ normalize vendor payloads to UI model
 *
 * No legacy `/api/team-members` references anywhere in this file.
 */
import React, { useEffect, useState, useMemo } from 'react';
import {
  RefreshCw, UserPlus, ShieldCheck, BadgeCheck, Edit, KeyRound,
  Trash2, Loader2, AlertTriangle, Users, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useTeamMembers } from '../hooks/useTeamMembers';
import * as teamApi from '../lib/api/teamApi';
import { useAuth } from '../contexts/AuthContext';

const initials = (name = '') =>
  name.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

const NON_MEDICAL_ROLES = [
  'Front Desk', 'Biller', 'Office Manager', 'Medical Assistant', 'Intake Coordinator',
];

export default function TeamManagement() {
  const { user: me, hasPermission } = useAuth();
  const {
    members, loading, error, refresh,
    createMember, setSchedulable, linkNpi, unlinkNpi, deleteMember, setRole,
  } = useTeamMembers();

  const canManage = hasPermission?.('team:admin') ?? true;

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(null);   // memberId currently mutating
  const [npiDraft, setNpiDraft] = useState({}); // memberId → {open, value}

  useEffect(() => { /* hook auto-hydrates on mount */ }, []);

  const onRefresh = async () => {
    await refresh();
    toast.success('Team refreshed');
  };

  const onAdd = async (e) => {
    e.preventDefault();
    setBusy('add');
    try {
      await createMember({ name: form.name.trim(), email: form.email.trim().toLowerCase(),
                           password: form.password, role: 'user' });
      toast.success(`Invited ${form.name}`);
      setForm({ name: '', email: '', password: '' });
      setAddOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.message || 'Failed to invite user');
    } finally {
      setBusy(null);
    }
  };

  const onToggleSchedulable = async (m) => {
    setBusy(m.id);
    try {
      await setSchedulable(m.id, !m.is_schedulable);
      toast.success(`${m.name} is now ${!m.is_schedulable ? 'schedulable' : 'not schedulable'}`);
    } catch (err) { toast.error(err?.message || 'Toggle failed'); }
    finally { setBusy(null); }
  };

  const onLinkNpi = async (m) => {
    const npi = (npiDraft[m.id]?.value || '').trim();
    if (!npi || !/^\d{10}$/.test(npi)) { toast.error('NPI must be 10 digits'); return; }
    setBusy(m.id);
    try {
      await linkNpi(m.id, npi);
      toast.success(`NPI ${npi} linked to ${m.name}`);
      setNpiDraft((d) => ({ ...d, [m.id]: { open: false, value: '' } }));
    } catch (err) { toast.error(err?.message || 'Link NPI failed'); }
    finally { setBusy(null); }
  };

  const onUnlinkNpi = async (m) => {
    if (!window.confirm(`Unlink NPI ${m.npi} from ${m.name}?`)) return;
    setBusy(m.id);
    try {
      await unlinkNpi(m.id);
      toast.success('NPI unlinked');
    } catch (err) { toast.error(err?.message || 'Unlink failed'); }
    finally { setBusy(null); }
  };

  const onResetPassword = async (m) => {
    const pw = window.prompt(`New password for ${m.name} (min 8 chars):`);
    if (!pw || pw.length < 8) { toast.error('Password must be ≥8 chars'); return; }
    setBusy(m.id);
    try {
      await teamApi.resetPassword(m.id, pw);
      toast.success(`Password reset for ${m.name}`);
    } catch (err) { toast.error(err?.message || 'Reset failed'); }
    finally { setBusy(null); }
  };

  const onDelete = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team?`)) return;
    setBusy(m.id);
    try {
      await deleteMember(m.id);
      toast.success(`${m.name} removed`);
    } catch (err) { toast.error(err?.message || 'Delete failed'); }
    finally { setBusy(null); }
  };

  const onChangeRole = async (m, role) => {
    setBusy(m.id);
    try {
      await setRole(m.id, role);
      toast.success(`Role updated → ${role}`);
    } catch (err) { toast.error(err?.message || 'Role update failed'); }
    finally { setBusy(null); }
  };

  const count = members?.length ?? 0;
  const myEmail = (me?.email || '').toLowerCase();

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="team-management">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" data-testid="team-title">Team Management</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage users in your Healthcare team</p>
            <span
              className="inline-block mt-2 px-2.5 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-full"
              data-testid="team-member-count"
            >
              {count} member{count !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50"
              data-testid="team-refresh-btn"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {canManage && (
              <button
                type="button"
                onClick={() => setAddOpen(!addOpen)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                data-testid="team-add-user-btn"
              >
                <UserPlus className="h-4 w-4" />
                Add User
              </button>
            )}
          </div>
        </div>

        {/* Add User panel */}
        {addOpen && canManage && (
          <form
            onSubmit={onAdd}
            className="p-4 bg-blue-50/40 border border-blue-100 rounded-xl space-y-3"
            data-testid="team-add-form"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-800">Add Team Member</p>
              <button type="button" onClick={() => setAddOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full Name"
                className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
                data-testid="team-add-name"
              />
              <input
                required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
                data-testid="team-add-email"
              />
              <input
                required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Password (min 8)"
                className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
                data-testid="team-add-password"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                type="submit" disabled={busy === 'add'}
                className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg"
                data-testid="team-add-submit"
              >
                {busy === 'add' ? 'Adding…' : 'Add User'}
              </button>
              <button
                type="button" onClick={() => setAddOpen(false)}
                className="px-3 py-1.5 text-sm bg-white border border-slate-200 text-slate-700 rounded-lg"
                data-testid="team-add-cancel"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Loading / Error / Empty */}
        {loading && !count && (
          <div className="py-12 text-center" data-testid="team-loading">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" />
            <p className="text-sm text-slate-500 mt-2">Loading team…</p>
          </div>
        )}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2" data-testid="team-error">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <p className="text-sm text-red-700">{error.message || 'Failed to load team'}</p>
          </div>
        )}
        {!loading && !error && count === 0 && (
          <div className="py-12 text-center" data-testid="team-empty">
            <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No team members yet.</p>
          </div>
        )}

        {/* Member grid */}
        {count > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="team-member-grid">
            {members.map((m) => (
              <MemberCard
                key={m.id}
                m={m}
                canManage={canManage}
                isSelf={m.email?.toLowerCase() === myEmail}
                busy={busy === m.id}
                npiDraft={npiDraft[m.id] || { open: false, value: '' }}
                setNpiDraft={(v) => setNpiDraft((d) => ({ ...d, [m.id]: v }))}
                onToggleSchedulable={() => onToggleSchedulable(m)}
                onLinkNpi={() => onLinkNpi(m)}
                onUnlinkNpi={() => onUnlinkNpi(m)}
                onResetPassword={() => onResetPassword(m)}
                onDelete={() => onDelete(m)}
                onChangeRole={(role) => onChangeRole(m, role)}
              />
            ))}
          </div>
        )}

        {!canManage && count > 0 && (
          <p className="text-xs text-slate-400 text-center" data-testid="team-readonly-banner">
            You have view-only access — contact an admin to make changes.
          </p>
        )}
      </div>
    </div>
  );
}


function MemberCard({ m, canManage, isSelf, busy, npiDraft, setNpiDraft,
                     onToggleSchedulable, onLinkNpi, onUnlinkNpi,
                     onResetPassword, onDelete, onChangeRole }) {
  const isMedical = m.category === 'medical';
  const roleLabel = useMemo(() => {
    if (isMedical) return (m.role || '').toUpperCase();
    return m.non_medical_role || m.role || '';
  }, [isMedical, m.role, m.non_medical_role]);

  return (
    <div
      className="p-4 bg-white border border-slate-200 rounded-xl space-y-3 relative"
      data-testid={`team-card-${m.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold shrink-0">
          {initials(m.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-semibold text-slate-900 truncate">{m.name}</p>
            {isSelf && <Pill className="bg-slate-100 text-slate-600" testid={`team-self-${m.id}`}>Self</Pill>}
            {m.is_team_owner && <Pill className="bg-amber-100 text-amber-800" testid={`team-owner-${m.id}`}>Owner</Pill>}
            {m.is_active && <Pill className="bg-emerald-100 text-emerald-700" testid={`team-active-${m.id}`}>Active</Pill>}
          </div>
          <p className="text-xs text-slate-500 truncate">{m.email}</p>
        </div>
        {canManage && !isSelf && (
          <div className="flex items-center gap-0.5 shrink-0">
            <IconBtn onClick={onResetPassword} title="Reset password" testid={`team-reset-pw-${m.id}`}>
              <KeyRound className="h-3.5 w-3.5 text-slate-500" />
            </IconBtn>
            <IconBtn onClick={onDelete} title="Remove from team" testid={`team-delete-${m.id}`}>
              <Trash2 className="h-3.5 w-3.5 text-red-500" />
            </IconBtn>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {/* Role badge — editable only for non-medical by admins */}
        {!isMedical && canManage ? (
          <select
            value={m.non_medical_role || ''}
            onChange={(e) => onChangeRole(e.target.value)}
            disabled={busy}
            className="text-xs px-2 py-1 bg-slate-50 border border-slate-200 rounded-md focus:outline-none"
            data-testid={`team-role-select-${m.id}`}
          >
            <option value="">(role)</option>
            {NON_MEDICAL_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        ) : (
          <Pill className="bg-slate-100 text-slate-700 uppercase text-[10px] tracking-wide" testid={`team-role-${m.id}`}>
            <ShieldCheck className="h-3 w-3 inline mr-1" />
            {roleLabel || '—'}
          </Pill>
        )}

        <Pill
          className={isMedical ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}
          testid={`team-category-${m.id}`}
        >
          {isMedical ? 'Medical' : 'Non-Medical'}
        </Pill>

        {/* Schedulable toggle */}
        <label
          className={`inline-flex items-center gap-1.5 text-xs ${canManage ? 'cursor-pointer' : 'opacity-70'}`}
          data-testid={`team-schedulable-label-${m.id}`}
        >
          <input
            type="checkbox"
            checked={!!m.is_schedulable}
            onChange={canManage ? onToggleSchedulable : undefined}
            disabled={!canManage || busy}
            className="h-3.5 w-3.5 accent-blue-600"
            data-testid={`team-schedulable-toggle-${m.id}`}
          />
          <span className={m.is_schedulable ? 'text-emerald-700 font-medium' : 'text-slate-500'}>
            {m.is_schedulable ? 'Schedulable' : 'Not schedulable'}
          </span>
        </label>
      </div>

      {/* NPI row */}
      {isMedical && (
        <div className="pt-2 border-t border-slate-100 flex items-center gap-2 flex-wrap">
          {m.npi ? (
            <>
              <Pill className="bg-emerald-50 text-emerald-700" testid={`team-npi-linked-${m.id}`}>
                <BadgeCheck className="h-3 w-3 inline mr-1" />
                NPI {m.npi} · Linked
              </Pill>
              {canManage && (
                <button
                  type="button" onClick={onUnlinkNpi} disabled={busy}
                  className="text-xs text-red-600 hover:text-red-700"
                  data-testid={`team-npi-unlink-${m.id}`}
                >
                  Unlink
                </button>
              )}
            </>
          ) : (
            canManage ? (
              !npiDraft.open ? (
                <button
                  type="button"
                  onClick={() => setNpiDraft({ open: true, value: '' })}
                  className="text-xs text-blue-600 hover:text-blue-700"
                  data-testid={`team-npi-link-btn-${m.id}`}
                >
                  Link NPI
                </button>
              ) : (
                <>
                  <input
                    autoFocus value={npiDraft.value}
                    onChange={(e) => setNpiDraft({ open: true, value: e.target.value })}
                    placeholder="10-digit NPI"
                    className="px-2 py-1 text-xs border border-slate-200 rounded-md focus:outline-none focus:border-blue-400"
                    data-testid={`team-npi-input-${m.id}`}
                  />
                  <button
                    type="button" onClick={onLinkNpi} disabled={busy}
                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    data-testid={`team-npi-save-${m.id}`}
                  >
                    Link
                  </button>
                  <button
                    type="button" onClick={() => setNpiDraft({ open: false, value: '' })}
                    className="px-2 py-1 text-xs bg-white border border-slate-200 text-slate-700 rounded-md"
                  >
                    Cancel
                  </button>
                </>
              )
            ) : (
              <span className="text-xs text-slate-400" data-testid={`team-npi-none-${m.id}`}>
                NPI not linked
              </span>
            )
          )}
        </div>
      )}
    </div>
  );
}


function Pill({ children, className = '', testid }) {
  return (
    <span
      className={`px-2 py-0.5 text-[10px] font-medium rounded-full inline-flex items-center ${className}`}
      data-testid={testid}
    >
      {children}
    </span>
  );
}


function IconBtn({ onClick, title, children, testid }) {
  return (
    <button
      type="button" onClick={onClick} title={title}
      className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-slate-100"
      data-testid={testid}
    >
      {children}
    </button>
  );
}
